<template>
  <div class="textarea-container">
    <label v-if="label" class="textarea-label" :for="inputId">{{ label }}<vue-feather v-if="tooltip" type="info" size="14" class="label-tooltip-icon" v-tooltip="tooltip" /></label>
    <div class="textarea-wrapper" :class="wrapperClass">
      <textarea :id="inputId" class="textarea-field" :class="{ 'textarea-field--resizable': resizable }" :placeholder="placeholder" :value="modelValue" :disabled="disabled"
        rows="1" autocomplete="nope" data-lpignore="true" data-form-type="other" @input="handleInput"
        @focus="isFocused = true" @blur="handleBlur" ref="textarea"></textarea>
    </div>
    <span v-if="error" class="textarea-error">{{ error }}</span>
  </div>
</template>

<script>
import VueFeather from 'vue-feather'

let inputId = 0

export default {
  name: 'ResizingTextArea',
  components: {
    'vue-feather': VueFeather
  },
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    minRows: {
      type: Number,
      default: 1
    },
    maxRows: {
      type: Number,
      default: 0 // 0 means no max
    },
    disabled: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ''
    },
    tooltip: {
      type: String,
      default: ''
    },
    resizable: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      inputId: `textarea-${++inputId}`,
      isFocused: false
    }
  },
  computed: {
    wrapperClass() {
      return {
        'textarea-wrapper--focused': this.isFocused,
        'textarea-wrapper--disabled': this.disabled,
        'textarea-wrapper--error': this.error
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.resize()
    })
  },
  watch: {
    modelValue() {
      this.$nextTick(() => this.resize())
    }
  },
  methods: {
    handleInput(event) {
      this.$emit('update:modelValue', event.target.value)
      this.$nextTick(() => this.resize())
    },

    handleBlur() {
      this.isFocused = false
      this.$emit('blur')
    },

    resize() {
      const textarea = this.$refs.textarea
      if (!textarea) return

      const minHeight = 43 + (21 * (this.minRows - 1))
      const maxHeight = this.maxRows > 0 ? 43 + (21 * (this.maxRows - 1)) : Infinity

      // Save scroll position of nearest scrollable ancestor before measurement
      const scrollParent = this.getScrollParent(textarea)
      const savedScrollTop = scrollParent ? scrollParent.scrollTop : 0

      // Measure content
      textarea.style.height = `${minHeight}px`
      const scrollHeight = textarea.scrollHeight

      // Apply height
      const height = Math.max(minHeight, Math.min(scrollHeight, maxHeight))
      textarea.style.height = `${height}px`
      textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden'

      // Restore scroll position
      if (scrollParent) scrollParent.scrollTop = savedScrollTop
    },

    getScrollParent(el) {
      let node = el.parentElement
      while (node) {
        const overflowY = getComputedStyle(node).overflowY
        if (overflowY === 'auto' || overflowY === 'scroll') return node
        node = node.parentElement
      }
      return null
    },

    focus() {
      this.$refs.textarea.focus()
    },

    blur() {
      this.$refs.textarea.blur()
    }
  },
  emits: ['update:modelValue', 'blur']
}
</script>

<style scoped>
.textarea-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.textarea-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 4px;
}

.label-tooltip-icon {
  color: #9ca3af;
  cursor: help;
  flex-shrink: 0;
}

.textarea-wrapper {
  position: relative;
  display: flex;
  background: #FFF;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  box-sizing: border-box;
  transition: all 0.15s ease;
}

.textarea-wrapper:hover:not(.textarea-wrapper--disabled):not(.textarea-wrapper--error) {
  border-color: #9ca3af;
}

.textarea-wrapper--focused:not(.textarea-wrapper--error) {
  border-color: var(--color-primary, #6a16f0);
  box-shadow: 0 0 0 3px rgba(106, 22, 240, 0.1);
}

.textarea-wrapper--disabled {
  background: #fafafa;
  cursor: not-allowed;
}

.textarea-wrapper--error {
  border-color: #dc2626;
}

.textarea-wrapper--error.textarea-wrapper--focused {
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
}

.textarea-field {
  width: 100%;
  height: 43px;
  padding: 12px 14px 10px;
  font-size: 1rem;
  font-weight: 400;
  font-family: inherit;
  line-height: 21px;
  color: #374151;
  background: transparent;
  border: none;
  outline: none;
  border-radius: 4px;
  box-sizing: border-box;
  resize: none;
  overflow: hidden;
}

.textarea-field--resizable {
  resize: horizontal;
}

.textarea-field::placeholder {
  color: #9ca3af;
}

.textarea-field:disabled {
  cursor: not-allowed;
}

.textarea-error {
  font-size: 0.8125rem;
  color: #dc2626;
}
</style>
